<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { idNameProblem, normalizeIdName } from '@/lib/idName';
import { useSecretsStore } from '@/stores/secrets';
import { useEnvironmentsStore } from '@/stores/environments';

// Create-mode multi-slot dialog. Captures `scope:name` plus per-slot values (or tombstones)
// in one form, then writes them all in slot order on Save All. Mirrors SecretEditor's grid
// shape but is stripped down — every slot starts empty so there's no Reveal / Hide / Delete
// / History affordance to render. The base-required invariant from the server is enforced
// client-side too: Save All is disabled until `_base` is either a real value or a tombstone.
//
// On success the parent (SecretsView) closes the dialog and refreshes the list.

const BASE_SLOT = '_base';

// `prefillReference` seeds scope:name when opened from the "Create secret" quick-fix.
// `prefillSlotValue` seeds one slot row's value when opened from the "Create secret from
// this value" code action (the extracted literal goes into the edited file's slot).
// Both empty/undefined → normal blank create (the SecretsView "New secret" button).
const props = defineProps<{
  visible: boolean;
  prefillReference?: string;
  prefillSlotValue?: { slot: string; value: string };
}>();
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  // Carries the created scope:name so the "from value" caller can rewrite the editor.
  (e: 'created', reference: string): void;
}>();

interface SlotRow {
  slot: string;
  value: string;
  tombstone: boolean;
}

const store = useSecretsStore();
const envStore = useEnvironmentsStore();
const toast = useToast();
const { list } = storeToRefs(store);
const { slotChoices } = storeToRefs(envStore);

const scopeColonName = ref('');
const rows = ref<SlotRow[]>([]);
const busy = ref(false);
const submitError = ref<string | null>(null);

// Build rows lazily so they pick up any environments added since the dialog last opened.
function rebuildRows() {
  rows.value = slotChoices.value.map((slot) => ({ slot, value: '', tombstone: false }));
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      scopeColonName.value = props.prefillReference
        ? sanitizeReference(props.prefillReference)
        : '';
      submitError.value = null;
      rebuildRows();
      const pf = props.prefillSlotValue;
      if (pf) {
        // Seed the edited file's slot; fall back to _base if that slot isn't a
        // registered choice (e.g. an unexpected disk name).
        const target = rows.value.find((r) => r.slot === pf.slot)
          ?? rows.value.find((r) => r.slot === BASE_SLOT);
        if (target) {
          target.value = pf.value;
          target.tombstone = false;
        }
      }
    }
  },
  { immediate: true },
);

// A secret reference is scope:name where EACH segment independently satisfies the
// shared id rule — but secrets are case-PRESERVING (allowUppercase), unlike the
// lowercase-only folder-backed entities. Live transform: split on the FIRST ':',
// normalise each side, rejoin with ':'. Trailing '-' is kept live (a space mid-word)
// and trimmed on submit.
const SECRET_SEGMENT_OPTS = { allowUppercase: true } as const;

function sanitizeReference(value: string, opts: { trimTrailingDash?: boolean } = {}): string {
  const segOpts = { ...opts, ...SECRET_SEGMENT_OPTS };
  const idx = value.indexOf(':');
  if (idx === -1) {
    // No separator typed yet — treat the whole thing as the scope-in-progress.
    return normalizeIdName(value, segOpts);
  }
  const scope = normalizeIdName(value.slice(0, idx), segOpts);
  const name = normalizeIdName(value.slice(idx + 1), segOpts);
  return `${scope}:${name}`;
}

function refFormatProblem(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parts = trimmed.split(':');
  if (parts.length !== 2) {
    return 'Must be in the form scope:name (two segments separated by a single colon).';
  }
  const scopeProblem = idNameProblem(parts[0], SECRET_SEGMENT_OPTS);
  if (parts[0].trim() !== '' && scopeProblem) return `Scope ${scopeProblem.replace(/^Name /, '')}`;
  const nameProblem = idNameProblem(parts[1], SECRET_SEGMENT_OPTS);
  if (parts[1].trim() !== '' && nameProblem) return `Name ${nameProblem.replace(/^Name /, '')}`;
  if (parts[0].trim() === '' || parts[1].trim() === '') {
    return 'Both scope and name are required (scope:name).';
  }
  return null;
}

const refFormatError = computed(() => refFormatProblem(scopeColonName.value));

const refDuplicateError = computed<string | null>(() => {
  const trimmed = scopeColonName.value.trim().toLowerCase();
  if (trimmed === '' || refFormatProblem(scopeColonName.value) !== null) return null;
  const exists = list.value.some(
    (s) => `${s.scope}:${s.name}`.toLowerCase() === trimmed,
  );
  return exists ? `A secret '${scopeColonName.value.trim()}' already exists.` : null;
});

const refError = computed(() => refFormatError.value ?? refDuplicateError.value);

const baseRow = computed(() => rows.value.find((r) => r.slot === BASE_SLOT) ?? null);
const baseProvided = computed(
  () => baseRow.value !== null && (baseRow.value.tombstone || baseRow.value.value !== ''),
);

const anyDirty = computed(() =>
  rows.value.some((r) => r.tombstone || r.value !== ''),
);

const blockReason = computed<string | null>(() => {
  if (refError.value !== null) return null; // name error already shown inline
  if (!anyDirty.value) return null;
  if (!baseProvided.value) {
    return 'Every secret must have a _base entry. Set a value for _base, or use Override with null, before saving.';
  }
  return null;
});

const canSubmit = computed(
  () =>
    !busy.value
    && scopeColonName.value.trim() !== ''
    && refError.value === null
    && baseProvided.value
    && blockReason.value === null,
);

function toggleTombstone(row: SlotRow) {
  if (!row.tombstone) {
    row.tombstone = true;
    row.value = '';
  } else {
    row.tombstone = false;
  }
}

function close() {
  if (busy.value) return;
  emit('update:visible', false);
}

async function submit() {
  if (!canSubmit.value) return;
  const ref = sanitizeReference(scopeColonName.value, { trimTrailingDash: true });
  // Write _base first so the server's base-required invariant is satisfied before any
  // env-slot writes hit. `slotChoices` already lists _base first; iterate in that order and
  // skip rows the operator left empty.
  busy.value = true;
  submitError.value = null;
  let saved = 0;
  let failed = 0;
  let firstError: string | null = null;
  for (const row of rows.value) {
    if (!row.tombstone && row.value === '') continue;
    const writeValue = row.tombstone ? '' : row.value;
    try {
      await store.save(row.slot, ref, writeValue);
      saved++;
    } catch (e) {
      failed++;
      if (firstError === null) firstError = (e as Error).message;
    }
  }
  busy.value = false;

  if (failed > 0) {
    submitError.value = firstError ?? 'Save failed';
    toast.add({
      severity: 'error',
      summary: `${failed} slot${failed === 1 ? '' : 's'} failed to save`,
      life: 5000,
    });
  }
  if (saved > 0) {
    toast.add({
      severity: 'success',
      summary: saved === 1 ? 'Secret created' : `${saved} slots written`,
      detail: ref,
      life: 2500,
    });
  }
  if (failed === 0 && saved > 0) {
    emit('created', ref);
    emit('update:visible', false);
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!busy"
    :close-on-escape="!busy"
    :style="{ width: '820px' }"
    @update:visible="(v: boolean) => { if (!v) close(); }"
  >
    <template #header>
      <span>New secret</span>
    </template>

    <p class="hint">
      Enter <code class="mono">scope:name</code> plus a value (or override-with-null) for any
      slots you want to populate now. <code class="mono">_base</code> is required; other slots
      are optional and inherit the base value at compose time unless overridden. Leave a slot
      blank to skip it.
    </p>

    <div class="field-stack">
      <label class="field-label" for="new-secret-ref">Reference (<code class="mono">scope:name</code>)</label>
      <InputText
        id="new-secret-ref"
        :model-value="scopeColonName"
        placeholder="scope:name"
        class="mono"
        fluid
        :disabled="busy"
        :invalid="refError !== null"
        @update:model-value="(v: string | undefined) => { scopeColonName = sanitizeReference(v ?? ''); }"
      />
      <p v-if="refError" class="inline-error">{{ refError }}</p>
    </div>

    <div class="slot-grid">
      <template v-for="row in rows" :key="row.slot">
        <span class="slot-label mono">{{ row.slot }}</span>
        <div class="cell value-cell">
          <span v-if="row.tombstone" class="tombstone-pill mono">
            <i class="pi pi-ban" />&nbsp;→ null (overrides base)
          </span>
          <InputText
            v-else
            type="password"
            v-model="row.value"
            :placeholder="row.slot === '_base' ? 'Required: value for _base' : 'Optional: value for this env'"
            class="mono"
            fluid
            autocomplete="off"
            spellcheck="false"
            :name="`new-secret-${row.slot}`"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            :disabled="busy || refError !== null"
          />
        </div>
        <div class="cell">
          <Button
            :label="row.tombstone ? 'Restore' : 'Override with null'"
            :icon="row.tombstone ? 'pi pi-undo' : 'pi pi-ban'"
            severity="secondary"
            size="small"
            outlined
            :disabled="busy || refError !== null"
            :title="row.tombstone
              ? 'Revert this slot to a normal value field'
              : 'Override with null: this slot will resolve to JSON null even if base has a value'"
            @click="toggleTombstone(row)"
          />
        </div>
      </template>
    </div>

    <Message v-if="blockReason" severity="warn" :closable="false" class="block-message">
      {{ blockReason }}
    </Message>
    <Message v-if="submitError" severity="error" :closable="false" class="block-message">{{ submitError }}</Message>

    <template #footer>
      <Button label="Cancel" severity="secondary" :disabled="busy" @click="close" />
      <Button
        label="Create"
        icon="pi pi-plus"
        :loading="busy"
        :disabled="!canSubmit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
.hint { color: var(--retail-hint-text); font-size: 0.8125rem; margin: 0 0 0.75rem 0; }
.hint code { font-family: 'Cascadia Code', Consolas, monospace; background: var(--retail-code-bg); padding: 0.05rem 0.3rem; border-radius: 3px; }
.field-stack { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1rem; }
.field-label { font-size: 0.8125rem; color: var(--p-form-field-label-color, #374151); }
.inline-error { margin: 0; font-size: 0.8125rem; color: var(--retail-error-text); }
.block-message { margin: 0.75rem 0 0 0; }

/* Three-column grid mirrors the right-hand columns of SecretEditor (slot | value | override).
   Reveal/Hide/Delete/History don't appear here — nothing exists server-side to reveal,
   delete, or look up history for. */
.slot-grid {
  display: grid;
  grid-template-columns: auto 1fr 12rem;
  row-gap: 0.4rem;
  column-gap: 0.5rem;
  align-items: center;
}
.slot-label {
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #374151);
  padding-right: 0.25rem;
  min-width: 5rem;
}
.cell { display: flex; align-items: center; width: 100%; }
.cell :deep(.p-button) { width: 100%; justify-content: center; }
.value-cell { width: 100%; }

.tombstone-pill {
  display: inline-flex;
  align-items: center;
  height: 2.25rem;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #6b7280);
  background: var(--retail-code-bg);
  border: 1px dashed var(--p-content-border-color, #d1d5db);
  border-radius: 4px;
  flex: 1;
}
</style>
